import { ACCESS_BLOCKED_MESSAGE } from './printing-errors';
import { formatMoment, problemText } from './printing-format';
import { PRINTING_AGENTS_PATH, PRINTING_SETTINGS_PATH } from './printing-paths';
import type { PrintingStatus } from '../types/printing.types';

/** Texto da faixa no desligamento geral, igual com a loja pausada ou não. */
export const PLATFORM_DISABLED_BANNER =
  'Impressão desativada temporariamente pela Vapt: os pedidos não estão saindo no papel e precisam ser aceitos à mão';

const SETTINGS_PATH = PRINTING_SETTINGS_PATH;

/**
 * Change print-agent: o computador recusado por protocolo continua ligado e conectado,
 * mas não imprime. "Offline" mandaria a equipe religar um computador que está ligado.
 */
export const UPDATE_REQUIRED_BANNER =
  'Computador da impressão precisa ser atualizado: os pedidos não estão saindo no papel';

/** Para quem não pode pedir a atualização: quem pode. */
export const UPDATE_REQUIRED_ASK_OWNER = 'Avise o dono ou o gerente para atualizar o computador.';

export type PrintingNotice =
  | { kind: 'banner'; lines: string[]; link: { to: string; label: string } | null }
  | { kind: 'paused'; text: string; link: { to: string; label: string } | null }
  | null;

/**
 * Decide o que o `PanelLayout` mostra sobre a impressão. A ordem importa:
 * - inativa (nunca ativada ou desativada pela loja): nada;
 * - assinatura pendente e desligamento geral vencem a pausa, porque retomar
 *   não faz o papel voltar a sair nesses casos;
 * - pausada: o aviso discreto, e os problemas de computador, impressora e
 *   cupom ficam de fora (a API continua a calculá-los);
 * - ativa e sem pausa: a faixa dos problemas.
 */
export function printingNotice(
  status: PrintingStatus,
  can: { settingsWrite: boolean; billingRead: boolean },
): PrintingNotice {
  if (!status.active) return null;

  const settingsLink = can.settingsWrite ? { to: SETTINGS_PATH, label: 'Abrir a aba Impressão' } : null;

  if (status.accessBlocked) {
    return {
      kind: 'banner',
      lines: [ACCESS_BLOCKED_MESSAGE],
      link: can.billingRead ? { to: '/billing', label: 'Ver assinatura' } : null,
    };
  }

  if (status.platformDisabled) {
    return { kind: 'banner', lines: [PLATFORM_DISABLED_BANNER], link: settingsLink };
  }

  if (status.paused) {
    const since = status.pausedAt ? ` desde ${formatMoment(status.pausedAt)}` : '';
    return {
      kind: 'paused',
      text: `Impressão pausada${since}`,
      link: can.settingsWrite ? { to: SETTINGS_PATH, label: 'Retomar na aba Impressão' } : null,
    };
  }

  // Recusado por protocolo: o problema continua `agent_offline`, só o texto muda. Quem
  // pode pedir vai direto à lista de computadores, onde está "Atualizar"; os demais são
  // orientados a avisar quem pode.
  if (status.agent?.updateRequired === true) {
    const updateLine = can.settingsWrite
      ? UPDATE_REQUIRED_BANNER
      : `${UPDATE_REQUIRED_BANNER}. ${UPDATE_REQUIRED_ASK_OWNER}`;
    const others = status.problems.filter((problem) => problem.code !== 'agent_offline').map(problemText);
    return {
      kind: 'banner',
      lines: [updateLine, ...others],
      link: can.settingsWrite ? { to: PRINTING_AGENTS_PATH, label: 'Atualizar o computador' } : null,
    };
  }

  if (status.problems.length === 0) return null;
  return { kind: 'banner', lines: status.problems.map(problemText), link: settingsLink };
}

